import { Circle, Flex, FlexProps, Text, VStack } from '@chakra-ui/react';
import { PieChart } from '~/components/PieChart';
import { OptionType } from '~/types';
import { formatPercentage } from '~/utils';

import { Color } from '../types/color';
import {
  AGE_RANGES,
  CHART_COLORS,
  EMPTY_CHART_COLOR,
} from './AgesPieChart.constants';

interface Props extends FlexProps {
  ages: OptionType<number>[];
}

export const AgesPieChart: React.FC<Props> = ({ ages = [], ...props }) => {
  const restKey = 'rest';
  const totalSumData = 1;

  const total = ages.reduce((acc, item) => {
    return (acc += item.value);
  }, 0);

  const data = ages.map((age) => ({
    key: age.label,
    value: age.value,
  }));

  /**
   * Sometimes, the sum of the age distribution does not
   * add up to 100%. For example, if YouTube is offline,
   * this data will not be displayed, and the percentage will be lower.
   * The graph should reflect 100%,
   * so the rest of the percentage must be calculated.
   */
  if (total < totalSumData) {
    data.push({
      key: restKey,
      value: totalSumData - total,
    });
  }

  const colors: Color = ages.length > 0 ? CHART_COLORS : EMPTY_CHART_COLOR;

  return (
    <Flex {...props} marginTop={6}>
      <PieChart
        data={data}
        colors={Object.values(colors)}
        height="72px"
        width="72px"
        data-testid="age-pie-chart"
      />

      <VStack marginLeft={6} spacing={2} alignItems="flex-start">
        {ages.length > 0
          ? ages.map((ageDistribution, index) => (
              <Flex
                direction="row"
                alignItems="center"
                key={ageDistribution.label}
              >
                <Circle size={2} backgroundColor={CHART_COLORS[index]} />
                <Text
                  fontWeight="medium"
                  as="span"
                  color="primary.500"
                  fontSize="lg"
                  marginLeft={2}
                  lineHeight="19px"
                >
                  {ageDistribution.label}
                </Text>
                <Text
                  as="span"
                  marginLeft={2}
                  color="neutral.400"
                  fontSize="md"
                  lineHeight="17px"
                >
                  {formatPercentage(ageDistribution.value, undefined, 1)}
                </Text>
              </Flex>
            ))
          : AGE_RANGES.map((range) => (
              <Flex direction="row" alignItems="center" key={range}>
                <Circle size={2} backgroundColor="neutral.200" />
                <Text
                  fontWeight="medium"
                  as="span"
                  color="primary.500"
                  fontSize="lg"
                  marginLeft={2}
                  lineHeight="19px"
                >
                  {range}
                </Text>
                <Text
                  as="span"
                  marginLeft={2}
                  color="neutral.400"
                  fontSize="md"
                  lineHeight="17px"
                >
                  N/A
                </Text>
              </Flex>
            ))}
      </VStack>
    </Flex>
  );
};
